import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Plus, Pencil, Trash2, Syringe, CheckCircle, XCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdministrationRoutesPage() {
  const { hasPermission, isSuperAdmin } = useAuth();
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRoute, setEditingRoute] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [formData, setFormData] = useState({
    nombre: '',
    codigo: '',
    descripcion: '',
    orden: 0
  });

  const canCreate = hasPermission('medicamentos', 'crear') || isSuperAdmin;
  const canEdit = hasPermission('medicamentos', 'editar') || isSuperAdmin;
  const canDelete = hasPermission('medicamentos', 'eliminar') || isSuperAdmin;

  const fetchRoutes = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/clinical-maintainers/administration-routes`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { include_inactive: showInactive }
      });
      setRoutes(response.data);
    } catch (error) {
      console.error('Error al cargar vías:', error);
      toast.error('No se pudieron cargar las vías de administración');
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => {
    fetchRoutes();
  }, [fetchRoutes]);

  const handleOpenModal = (route = null) => {
    if (route) {
      setEditingRoute(route);
      setFormData({
        nombre: route.nombre || '',
        codigo: route.codigo || '',
        descripcion: route.descripcion || '',
        orden: route.orden || 0
      });
    } else {
      setEditingRoute(null);
      setFormData({
        nombre: '',
        codigo: '',
        descripcion: '',
        orden: routes.length + 1
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingRoute(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nombre.trim() || !formData.codigo.trim()) {
      toast.error('Nombre y código son requeridos');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (editingRoute) {
        await axios.put(`${API_URL}/api/clinical-maintainers/administration-routes/${editingRoute.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Vía actualizada');
      } else {
        await axios.post(`${API_URL}/api/clinical-maintainers/administration-routes`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Vía creada');
      }
      handleCloseModal();
      fetchRoutes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleToggleActive = async (route) => {
    if (route.is_default) {
      toast.error('No se pueden desactivar vías del sistema');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API_URL}/api/clinical-maintainers/administration-routes/${route.id}/toggle-active`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Vía ${route.is_active ? 'desactivada' : 'activada'}`);
      fetchRoutes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar estado');
    }
  };

  const handleDelete = async (route) => {
    if (route.is_default) {
      toast.error('No se pueden eliminar vías del sistema');
      return;
    }
    if (!window.confirm(`¿Eliminar vía "${route.nombre}"?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/clinical-maintainers/administration-routes/${route.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Vía eliminada');
      fetchRoutes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="administration-routes-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Syringe className="h-7 w-7 text-blue-600" />
            Vías de Administración
          </h1>
          <p className="text-gray-500">Gestión de vías de administración de medicamentos</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Switch
              checked={showInactive}
              onCheckedChange={setShowInactive}
              id="show-inactive"
            />
            <Label htmlFor="show-inactive" className="text-sm">Mostrar inactivas</Label>
          </div>
          {canCreate && (
            <Button onClick={() => handleOpenModal()} data-testid="add-route-btn">
              <Plus className="h-4 w-4 mr-2" />
              Nueva Vía
            </Button>
          )}
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[80px]">Código</TableHead>
                <TableHead>Nombre</TableHead>
                <TableHead>Descripción</TableHead>
                <TableHead className="w-[80px]">Orden</TableHead>
                <TableHead className="w-[100px]">Estado</TableHead>
                <TableHead className="w-[100px]">Tipo</TableHead>
                <TableHead className="w-[150px]">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                    Cargando...
                  </TableCell>
                </TableRow>
              ) : routes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                    No hay vías de administración
                  </TableCell>
                </TableRow>
              ) : (
                routes.map((route) => (
                  <TableRow key={route.id} className={!route.is_active ? 'opacity-50' : ''}>
                    <TableCell>
                      <Badge variant="outline" className="font-mono">{route.codigo}</Badge>
                    </TableCell>
                    <TableCell className="font-medium">{route.nombre}</TableCell>
                    <TableCell className="text-gray-600 text-sm">{route.descripcion || '-'}</TableCell>
                    <TableCell className="text-center">{route.orden}</TableCell>
                    <TableCell>
                      {route.is_active ? (
                        <Badge className="bg-green-100 text-green-800">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Activa
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="bg-gray-100 text-gray-600">
                          <XCircle className="h-3 w-3 mr-1" />
                          Inactiva
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {route.is_default ? (
                        <Badge variant="outline">Sistema</Badge>
                      ) : (
                        <Badge variant="secondary">Personalizada</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {canEdit && !route.is_default && (
                          <Button variant="ghost" size="sm" onClick={() => handleOpenModal(route)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                        )}
                        {canEdit && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleToggleActive(route)}
                            disabled={route.is_default}
                          >
                            {route.is_active ? (
                              <XCircle className="h-4 w-4 text-orange-500" />
                            ) : (
                              <CheckCircle className="h-4 w-4 text-green-500" />
                            )}
                          </Button>
                        )}
                        {canDelete && !route.is_default && (
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(route)}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingRoute ? 'Editar Vía de Administración' : 'Nueva Vía de Administración'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Código *</Label>
                <Input
                  value={formData.codigo}
                  onChange={(e) => setFormData(prev => ({ ...prev, codigo: e.target.value.toUpperCase() }))}
                  placeholder="Ej: IV, IM, VO"
                  required
                  maxLength={10}
                  data-testid="input-codigo"
                />
              </div>
              <div>
                <Label>Orden</Label>
                <Input
                  type="number"
                  value={formData.orden}
                  onChange={(e) => setFormData(prev => ({ ...prev, orden: parseInt(e.target.value) || 0 }))}
                  min={0}
                  data-testid="input-orden"
                />
              </div>
            </div>
            <div>
              <Label>Nombre *</Label>
              <Input
                value={formData.nombre}
                onChange={(e) => setFormData(prev => ({ ...prev, nombre: e.target.value }))}
                placeholder="Ej: Intravenosa, Intramuscular"
                required
                data-testid="input-nombre"
              />
            </div>
            <div>
              <Label>Descripción</Label>
              <Textarea
                value={formData.descripcion}
                onChange={(e) => setFormData(prev => ({ ...prev, descripcion: e.target.value }))}
                placeholder="Descripción de la vía de administración"
                rows={2}
                data-testid="input-descripcion"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseModal}>
                Cancelar
              </Button>
              <Button type="submit" data-testid="submit-route">
                {editingRoute ? 'Guardar Cambios' : 'Crear Vía'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
